import React, { useMemo, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '../Unicon';

/**
 * TimePicker (web-friendly) using Unistyles + theme
 * Props:
 * - date: Date | string | undefined
 * - onChange: (date: Date) => void
 * - minuteInterval?: number (default 1)
 */
const TimePicker = ({ date, onChange, minuteInterval = 1, forceInterval = false }) => {

  const baseDate = useMemo(() => {
    if (!date) return new Date();
    return date instanceof Date ? new Date(date) : new Date(date);
  }, [date]);

  const hour24 = baseDate.getHours();
  const minute = baseDate.getMinutes();
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = ((hour24 + 11) % 12) + 1; // 0->12, 13->1 etc

  const pad2 = (n) => n.toString().padStart(2, '0');

  // Local editable text states
  const [hourText, setHourText] = useState(pad2(hour12));
  const [minuteText, setMinuteText] = useState(pad2(minute));

  // Sync text fields when incoming date changes externally
  useEffect(() => {
    setHourText(pad2(hour12));
    setMinuteText(pad2(minute));
  }, [date]);

  const updateTime = (h12, min, ampmVal) => {
    const next = new Date(baseDate);
    const h24 = h12 % 12 + (ampmVal === 'PM' ? 12 : 0);
    next.setHours(h24, min, 0, 0);
    onChange?.(next);
  };

  const incHour = () => updateTime(((hour12 % 12) + 1), minute, ampm);
  const decHour = () => updateTime(((hour12 + 10) % 12) + 1, minute, ampm);

  const incMinute = () => {
    const step = Math.max(1, Number(minuteInterval) || 1);
    const nextMin = (Math.floor(minute / step) * step + step) % 60;
    const overflow = minute + step >= 60;
    let nextHour12 = hour12;
    let nextAmPm = ampm;
    if (overflow) {
      // carry hour forward; flip AM/PM when crossing 11 -> 12
      nextHour12 = ((hour12 % 12) + 1);
      if (hour12 === 11) {
        nextAmPm = ampm === 'AM' ? 'PM' : 'AM';
      }
    }
    updateTime(nextHour12, nextMin, nextAmPm);
  };

  const decMinute = () => {
    const step = Math.max(1, Number(minuteInterval) || 1);
    const nextMin = (Math.ceil(minute / step) * step - step + 60) % 60;
    const borrow = minute - step < 0;
    let nextHour12 = hour12;
    let nextAmPm = ampm;
    if (borrow) {
      // borrow hour; flip AM/PM when crossing 12 -> 11
      nextHour12 = ((hour12 + 10) % 12) + 1;
      if (hour12 === 12) {
        nextAmPm = ampm === 'AM' ? 'PM' : 'AM';
      }
    }
    updateTime(nextHour12, nextMin, nextAmPm);
  };

  const setAm = () => updateTime(hour12, minute, 'AM');
  const setPm = () => updateTime(hour12, minute, 'PM');

  const onHourChange = (val) => {
    const clean = (val || '').replace(/[^0-9]/g, '').slice(0, 2);
    setHourText(clean);
  };

  const onHourBlur = () => {
    let num = parseInt(hourText, 10);
    if (isNaN(num)) {
      num = hour12;
    }
    if (num === 0) num = 12;
    if (num < 1) num = 1;
    if (num > 12) num = 12;
    setHourText(pad2(num));
    updateTime(num, minute, ampm);
  };

  const onMinuteChange = (val) => {
    const clean = (val || '').replace(/[^0-9]/g, '').slice(0, 2);
    setMinuteText(clean);
  };

  const onMinuteBlur = () => {
    let num = parseInt(minuteText, 10);
    if (isNaN(num)) {
      num = minute;
    }
    if (num < 0) num = 0;
    if (num > 59) num = 59;

    if (forceInterval) {
      const step = Math.max(1, Number(minuteInterval) || 1);
      num = Math.floor(num / step) * step;
    }
    setMinuteText(pad2(num));
    updateTime(hour12, num, ampm);
  };

  return (
    <View style={styles.container}>
      <View style={styles.pickerRow}>
        <View style={styles.column}>
          <TouchableOpacity accessibilityLabel="Increase hour" onPress={incHour}>
            <Unicon name="chevron-up-outline" size={18} colorVariant="text" />
          </TouchableOpacity>
          <View style={styles.valueBox}>
            <TextInput
              value={hourText}
              onChangeText={onHourChange}
              onBlur={onHourBlur}
              keyboardType="number-pad"
              inputMode="numeric"
              returnKeyType="done"
              maxLength={2}
              style={styles.valueInput}
            />
          </View>
          <TouchableOpacity accessibilityLabel="Decrease hour" onPress={decHour}>
            <Unicon name="chevron-down-outline" size={18} colorVariant="text" />
          </TouchableOpacity>
        </View>

        <Text style={styles.separator}>:</Text>

        <View style={styles.column}>
          <TouchableOpacity accessibilityLabel="Increase minutes" onPress={incMinute}>
            <Unicon name="chevron-up-outline" size={18} colorVariant="text" />
          </TouchableOpacity>
          <View style={styles.valueBox}>
            <TextInput
              value={minuteText}
              onChangeText={onMinuteChange}
              onBlur={onMinuteBlur}
              keyboardType="number-pad"
              inputMode="numeric"
              returnKeyType="done"
              maxLength={2}
              style={styles.valueInput}
            />
          </View>
          <TouchableOpacity accessibilityLabel="Decrease minutes" onPress={decMinute}>
            <Unicon name="chevron-down-outline" size={18} colorVariant="text" />
          </TouchableOpacity>
        </View>

        <View style={[styles.ampmGroup]}>
          <TouchableOpacity
            accessibilityLabel="Set AM"
            onPress={setAm}
            style={[styles.ampmBtn, ampm === 'AM' ? styles.ampmBtnActive : null]}
          >
            <Text style={[styles.ampmText, ampm === 'AM' ? styles.ampmTextActive : null]}>AM</Text>
          </TouchableOpacity>
          <TouchableOpacity
            accessibilityLabel="Set PM"
            onPress={setPm}
            style={[styles.ampmBtn, ampm === 'PM' ? styles.ampmBtnActive : null]}
          >
            <Text style={[styles.ampmText, ampm === 'PM' ? styles.ampmTextActive : null]}>PM</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create(({colors, fonts, typography, roundness}) => ({
    container: {
      // flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 10,
    },
    pickerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    column: {
      alignItems: 'center',
    },
    valueBox: {
      marginVertical: 4,
      width: 58,
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderRadius: roundness.sm,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    valueText: {
      color: colors.text,
      fontFamily: fonts.heading,
      fontSize: 18,
    },
    valueInput: {
      color: colors.text,
      fontFamily: fonts.heading,
      fontSize: 18,
      textAlign: 'center',
      paddingVertical: 0,
      paddingHorizontal: 0,
      width: 58,
      outlineStyle: 'none',
      includeFontPadding: false,
      textAlignVertical: 'center',
    },
    separator: {
      color: colors.textSecondary,
      fontFamily: fonts.heading,
      fontSize: 20,
      paddingHorizontal: 2,
    },
    ampmGroup: {
      flexDirection: 'row',
      gap: 6,
      marginLeft: 8,
    },
    ampmBtn: {
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      _web: {
        transition: "all 0.15s ease",
        _hover: {
          borderColor: colors.outline,
        }
      }
    },
    ampmBtnActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    ampmText: {
      color: colors.text,
      fontFamily: fonts.body,
      fontSize: typography.body,
    },
    ampmTextActive: {
      color: colors.onPrimary,
      fontFamily: fonts.bold,
    },
  }));

export default TimePicker;
